import Loader from "components/common/Loader";
import ProjectNavBarBack from "components/layout/navigation/ProjectNavBarBack/ProjectNavBarBack";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchCampaignActivities } from "store/campaigns/campaignActivities/campaignActivities.actions";
import { fetchCampaignByShort } from "store/campaigns/campaignDetails/campaignDetails.actions";
import { fetchDonorLeaderboard } from "store/campaigns/campaignLeaderDonors/campaignLeaderDonors.actions"; 
import { fetchCampaignPosts } from "store/campaigns/campaignPosts/campaignPosts.actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import FundraisingDetailPosts from "../../components/FundraisingDetails/FundraisingDetailsRelatedPosts";
import OrganizationCard from "../../components/Projects/OrganizationCard/OrganizationCard";
import * as S from "./index.styles";

import FundraisingDetailMain from "../../components/FundraisingDetails/FundraisingDetailsMain";
import FundraisingStats from "../../components/FundraisingDetails/FundraisingStats";
import FundraisingDetailEvents from "../../components/FundraisingDetails/FundrainsingDetailsEvents";

const FundraisingDetails: React.FC = () => {
  const { fundraiserId } = useParams<{ fundraiserId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    data: campaignDetails,
    loading: campaignDetailsLoading,
    error: campaignDetailsError,
  } = useAppSelector((state) => state.campaigns.campaignDetails);
  const {
    data: campaignActivities,
    loading: campaignActivitiesLoading,
    error: campaignActivitiesError,
  } = useAppSelector((state) => state.campaigns.campaignActivities);
  const {
    data: campaignPosts,
    loading: campaignPostsLoading,
    error: campaignPostsError,
  } = useAppSelector((state) => state.campaigns.campaignPosts);
  const {
  data: campaignLeaderDonors,
  loading: campaignLeaderDonorsLoading,
  error: campaignLeaderDonorsError,
} = useAppSelector((state) => state.campaigns.campaignLeaderDonors);
  const user = useAppSelector((state) => state.user);
  useEffect(() => {
    const fetchData = async () => {
      if (!fundraiserId) {
        console.error("No fundraiser ID provided");
        return;
      }

      try {
        // First fetch campaign details
        await dispatch(fetchCampaignByShort(fundraiserId));

        // Get campaign ID from redux state
        const campaignId = campaignDetails.id;

        if (campaignId) {
          // Then fetch all other data in parallel using the campaign ID
          await Promise.all([
            dispatch(fetchCampaignActivities(campaignId)),
            dispatch(fetchCampaignPosts(campaignId)),
            dispatch(fetchDonorLeaderboard(campaignId)),
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch campaign data:", error);
      }
    };

    fetchData();
  }, [dispatch, fundraiserId, campaignDetails.id]);

  // Campaign data
  const title = campaignDetails.name;
  const organizer = campaignDetails.user?.name;
  const description = campaignDetails.description;
  const coverImage = campaignDetails.default_image;
  const currentRaised = Number(campaignDetails.total_raised) || 0;
  const goal = Number(campaignDetails.goal) || 0;
  const startDate = campaignDetails.start_date_time ?? "";
  const endDate = campaignDetails.end_date_time ?? "";
  const donors = Array.isArray(campaignLeaderDonors) ? campaignLeaderDonors: [];
  const projectId = Number(campaignDetails.project_id);
  /*const organization = {
    name: "Bee Conservation Alliance",
    description: "A nonprofit dedicated to protecting bees and their habitats.",
    logo: "https://images.unsplash.com/photo-1529612700005-e35377bf1415?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    website: "#",
  };*/

  const handleOnSeeAllActivities = () => {
    console.log("View all activities");
  };

  return (
    <S.PageContainer>
      <ProjectNavBarBack onClick={() => user.id === campaignDetails.user?.id ? navigate(`/org/projects/${projectId}`) : navigate(-1) }/>

      {user?.id === campaignDetails.user?.id && (
        <S.EditButton onClick={() => navigate(`/fundraising/${fundraiserId}/edit`)}>
          ✎ Edit this campaign
        </S.EditButton>
      )}

      <S.TwoColumnLayout>
        <S.LeftColumn>
          {campaignDetailsLoading ? (
            <Loader />
          ) : campaignDetailsError ? (
            <div>Error: {campaignDetailsError}</div>
          ) : (
            <FundraisingDetailMain
              title={title}
              organizer={organizer}
              description={description}
              image={coverImage}
              organizerAvatar={campaignDetails.user?.socialAvatarUrl || ""}
              location={campaignDetails.shortened_location}
            />
          )}
        </S.LeftColumn>

        <S.RightColumn>
          {campaignLeaderDonorsLoading ? (
            <Loader />
          ) : campaignLeaderDonorsError ? (
            <div>Error: {campaignLeaderDonorsError}</div>
          ) : (
            <FundraisingStats
              current={currentRaised}
              goal={goal}
              startDate={startDate}
              endDate={endDate}
              donors={donors}
              campaignId={fundraiserId!}
              activities={campaignActivities}
            />
          )}
          {/*organization && (
            <OrganizationCard
              name={organization.name}
              description={organization.description}
              logo={organization.logo}
              website={organization.website}
            />
          )}*/}
          {user && (
            <OrganizationCard
              name={user.name}
              description={user.bio_description || ""}
              logo={user.socialAvatarUrl || ""}
              website={"#"}
            />
          )}
          {campaignActivitiesLoading ? (
            <Loader />
          ) : campaignActivitiesError ? (
            <div>Error: {campaignActivitiesError}</div>
          ) : (
            <FundraisingDetailEvents
              events={campaignActivities}
              onSeeAll={handleOnSeeAllActivities}
            />
          )}
          {campaignPostsLoading ? (
            <Loader />
          ) : campaignPostsError ? (
            <div>Error: {campaignPostsError}</div>
          ) : (
            <FundraisingDetailPosts posts={campaignPosts} />
          )}
        </S.RightColumn>
      </S.TwoColumnLayout>
    </S.PageContainer>
  );
};

export default FundraisingDetails;
